import type { PaginatedResponse } from '@reliance/types/responses/PaginatedResponse';
import { PaginationFilters } from '@reliance/types/filters/PaginationFilters';
import { TextMessageFilters } from '@reliance/types/filters/TextMessageFilters';
import type { TextMessageStatus } from '@reliance/types/models/TextMessageStatus';
import { TextMessage } from '@reliance/types/models/TextMessage';
import moment from 'moment';
import { useAsync } from 'react-async-hook';
import api from '../common/api';

export type TextMessageStatusListFilters = PaginationFilters & {
  startDate?: string;
  endDate?: string;
  textMessageId?: number;
};

export const useTextMessages = (filters?: TextMessageFilters) => {
  const key = filters != null ? JSON.stringify(filters) : '';
  return useAsync<PaginatedResponse<TextMessage> | null>(
    () => {
      if (filters == null) {
        return Promise.resolve(null);
      }
      return api.get<PaginatedResponse<TextMessage>>('/text-message', { params: filters }).then((r) => r.data);
    },
    [key]
  );
};

export const useAppInviteTextMessages = (onlineUserId: number, filters?: PaginationFilters) => {
  return useAsync<PaginatedResponse<TextMessage>>(
    () => api.get<PaginatedResponse<TextMessage>>(`/onlineuser/${onlineUserId}/app-invite`, { params: filters }).then((r) => r.data),
    [onlineUserId, filters]
  );
};

export const useSupportMessages = (
  inmateId: number | null | undefined,
  includeActivityRecord: boolean = false,
  page: number = 1,
  perPage: number = 100
) => {
  return useAsync<PaginatedResponse<TextMessage>>(
    (currentInmateId?: number | null | undefined) => {
      const id = currentInmateId !== undefined ? currentInmateId : inmateId;
      if (!id) {
        return null;
      }
      const filters: TextMessageFilters = {
        inmates: String(id),
        types: 'Staff Message',
        startDate: moment().subtract(10, 'year').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        endDate: moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
        includeActivityRecord,
        orderBy: '-createdDate',
        page,
        perPage,
      };
      return api.get<PaginatedResponse<TextMessage>>('/text-message', { params: filters }).then((r) => r.data);
    },
    // Use empty array when inmateId is null to prevent auto-execution (for manual control)
    inmateId === null ? [] : [inmateId, includeActivityRecord, page, perPage]
  );
};

export const useInmateTextMessagesByInmateId = (
  inmateId: number | null | undefined,
  filters?: TextMessageFilters
) => {
  const filterKey = filters != null ? JSON.stringify(filters) : '';
  return useAsync<PaginatedResponse<TextMessage>>(
    (currentInmateId?: number | null | undefined) => {
      const id = currentInmateId !== undefined ? currentInmateId : inmateId;
      if (!id) {
        return null;
      }

      const params: TextMessageFilters = {
        inmates: String(id),
        ...filters,
      };

      return api.get<PaginatedResponse<TextMessage>>('/text-message', { params }).then((r) => r.data);
    },
    inmateId === null ? [] : [inmateId, filterKey]
  );
};

export const useTextMessageStatuses = (filters?: TextMessageStatusListFilters) => {
  const key = JSON.stringify(filters ?? {});
  return useAsync<PaginatedResponse<TextMessageStatus>>(
    () =>
      api
        .get<PaginatedResponse<TextMessageStatus>>('/text-message/status', { params: filters ?? {} })
        .then((r) => r.data),
    [key]
  );
};

